<template>
  <div class="label-tag-menu" ref="menuRef" :style="{ ...horizontalPosition, ...verticalPosition }">
    <div class="label-tag-menu__header">
      <div>{{ title }}</div>
      <div class="label-tag-menu__separator"></div>
    </div>
    <div class="label-tag-menu__search-bar" v-if="searchAllowed">
      <BaseMenuSearchBar
        ref="searchBarRef"
        name="search"
        type="text"
        @search="$emit('search', $event)"
        hasGrayBackground
      ></BaseMenuSearchBar>
    </div>
    <div class="label-tag-menu__items">
      <slot name="prepend-items"></slot>
      <div
        class="label-tag-menu__item"
        v-for="(item, index) in items"
        :key="item.id || index"
        :class="{
          'is-selected': selectedItem && selectedItem.id === item.id,
          'is-connected': isItemConnected && isItemConnected(item),
        }"
        @click="onItemClick(item)"
        @mouseover="selectItem(item)"
      >
        <span class="label-tag-menu__item-label">
          <slot name="item" :item="item">{{ item.label }}</slot>
        </span>
        <div class="label-tag-menu__buttons" v-if="showItemButtons && item === selectedItem && item.buttons">
          <div v-for="(button, buttonIndex) in item.buttons" :key="buttonIndex">
            <BaseToggleButtons
              :item="displayedButton(item, button)"
              @click.native="$emit('select-item-button', item, button)"
            >
              <slot name="button-label" :button="button">{{ button.name }}</slot>
            </BaseToggleButtons>
          </div>
        </div>
      </div>
      <div v-if="items.length === 0" class="label-tag-menu__item" style="color: grey; cursor: default">
        {{ emptyMessage }}
      </div>
      <div class="submenu" v-if="isSubMenuVisible" :style="subMenuHorizontalPosition">
        <div class="submenu__header">
          <div>{{ submenuTitle }}</div>
        </div>
        <div class="submenu__items">
          <div
            class="submenu__item"
            v-for="(choice, index) in sortedSubmenuItems"
            :key="index"
            @click="onSubmenuItemClick(selectedItem, choice)"
            :class="{
              'is-connected': isSubmenuItemConnected && isSubmenuItemConnected(selectedItem, choice),
              'is-disabled': isSubmenuItemDisabled && isSubmenuItemDisabled(selectedItem, choice),
              'is-hover': index === selectSubItemIndex,
            }"
            v-tooltip="
              isSubmenuItemDisabled && isSubmenuItemDisabled(selectedItem, choice) ? submenuItemDisabledTooltip : ''
            "
          >
            {{ choice }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import BaseToggleButtons from '@/components/base/BaseToggleButtons.vue';
import BaseMenuSearchBar from '@/components/pdf-viewer/BaseMenuSearchBar';

export default {
  name: 'SelectionLinkMenu',
  components: { BaseToggleButtons, BaseMenuSearchBar },
  props: {
    title: {
      type: String,
      required: true,
    },
    emptyMessage: {
      type: String,
      default: '',
    },
    items: {
      type: Array,
      default: () => [],
    },
    horizontalPosition: {
      type: Object,
    },
    verticalPosition: {
      type: Object,
    },
    searchAllowed: {
      type: Boolean,
      default: false,
    },
    submenuTitle: {
      type: String,
      default: '',
    },
    showItemButtons: {
      type: Boolean,
      default: false,
    },
    isItemConnected: {
      type: Function,
      default: null,
    },
    isSubmenuItemConnected: {
      type: Function,
      default: null,
    },
    isSubmenuItemDisabled: {
      type: Function,
      default: null,
    },
    submenuItemDisabledTooltip: {
      type: String,
      default: '',
    },
    displayedButton: {
      type: Function,
      default: (item, button) => button,
    },
  },
  data() {
    return {
      selectedItem: null,
      selectedIndex: -1,
      subItemSelected: false,
      selectSubItemIndex: -1,
      selectedButtonIndex: -1,
    };
  },
  computed: {
    sortedSubmenuItems() {
      if (!this.selectedItem || !this.selectedItem.choices) return [];
      return this.selectedItem.choices.slice().sort((a, b) => a.localeCompare(b));
    },
    isSubMenuVisible() {
      return this.selectedItem !== null && this.selectedItem.choices && this.selectedItem.choices.length > 0;
    },
    subMenuHorizontalPosition() {
      return this.horizontalPosition.left === '44px' ? { left: '270px' } : { left: '-240px' };
    },
  },
  methods: {
    selectItem(item) {
      this.selectedItem = item;
    },
    onItemClick(item) {
      if (this.showItemButtons && item.buttons) return;
      this.$emit('select-item', item);
    },
    onSubmenuItemClick(item, choice) {
      if (this.isSubmenuItemConnected && this.isSubmenuItemConnected(item, choice)) return;
      if (this.isSubmenuItemDisabled && this.isSubmenuItemDisabled(item, choice)) return;
      this.$emit('select-submenu-item', item, choice);
    },
    close() {
      this.$emit('close', true);
    },
    emitSize() {
      const width = this.isSubMenuVisible ? this.$refs.menuRef.clientWidth + 240 : this.$refs.menuRef.clientWidth;
      this.$emit('size-changed', {
        width: width + 30,
        height: this.$refs.menuRef.clientHeight,
      });
    },
    handleArrowKeys(event) {
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        this.removeFocus();
        if (this.subItemSelected) {
          this.selectSubItemIndex = Math.max(this.selectSubItemIndex - 1, 0);
        } else {
          this.selectedIndex = Math.max(this.selectedIndex - 1, 0);
        }
      } else if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.removeFocus();
        if (this.subItemSelected) {
          this.selectSubItemIndex = Math.min(this.selectSubItemIndex + 1, this.sortedSubmenuItems.length - 1);
        } else {
          this.selectedIndex = Math.min(this.selectedIndex + 1, this.items.length - 1);
        }
      } else if (event.key === 'Enter') {
        event.preventDefault();
        if (this.subItemSelected) {
          const selectedChoice = this.sortedSubmenuItems[this.selectSubItemIndex];
          if (selectedChoice) {
            this.onSubmenuItemClick(this.selectedItem, selectedChoice);
          }
        } else if (this.selectedIndex >= 0 && this.selectedIndex < this.items.length) {
          const selectedItem = this.items[this.selectedIndex];
          if (selectedItem.choices && selectedItem.choices.length > 0) {
            this.selectItem(selectedItem);
            this.subItemSelected = true;
            this.selectSubItemIndex = 0;
            return;
          } else if (this.showItemButtons && selectedItem.buttons) {
            const buttons = this.selectedItem.buttons;
            if (this.selectedButtonIndex !== -1) {
              this.$emit('select-item-button', this.selectedItem, buttons[this.selectedButtonIndex]);
            } else {
              buttons[0].selected = true;
            }
          } else {
            this.$emit('select-item', selectedItem);
          }
        }
      } else if (event.key === 'Escape' || event.key === 'Esc' || event.key === 'Backspace') {
        this.subItemSelected = false;
        this.selectSubItemIndex = -1;
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        if (this.selectedItem && this.selectedItem.buttons) {
          const buttons = this.selectedItem.buttons;
          if (this.selectedButtonIndex === -1) {
            this.selectedButtonIndex = 0;
          }
          buttons[this.selectedButtonIndex].selected = false;
          this.selectedButtonIndex = Math.max(this.selectedButtonIndex - 1, 0);
          buttons[this.selectedButtonIndex].selected = true;
        }
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        if (this.selectedItem && this.selectedItem.buttons) {
          const buttons = this.selectedItem.buttons;
          if (this.selectedButtonIndex === -1) {
            this.selectedButtonIndex = 0;
          }
          buttons[this.selectedButtonIndex].selected = false;
          this.selectedButtonIndex = Math.min(this.selectedButtonIndex + 1, buttons.length - 1);
          buttons[this.selectedButtonIndex].selected = true;
        }
      }

      if (this.selectedIndex >= 0 && this.selectedIndex < this.items.length && !this.subItemSelected) {
        this.selectItem(this.items[this.selectedIndex]);
      }
    },
    ensureFocus() {
      if (this.searchAllowed && this.$refs.menuRef && this.$refs.searchBarRef) {
        this.$nextTick(() => {
          this.$refs.searchBarRef.focusInput();
        });
      }
    },
    removeFocus() {
      if (this.searchAllowed && this.$refs.menuRef && this.$refs.searchBarRef) {
        this.$refs.searchBarRef.removeFocus();
      }
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.emitSize();
      window.addEventListener('resize', this.emitSize);
    });
    document.addEventListener('keydown', this.handleArrowKeys);
    const checkRef = () => {
      if (this.searchAllowed && this.$refs.searchBarRef) {
        this.$nextTick(() => {
          this.$refs.searchBarRef.focusInput();
        });
      } else {
        setTimeout(checkRef, 100);
      }
    };
    checkRef();
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.emitSize);
  },
  destroyed() {
    document.removeEventListener('keydown', this.handleArrowKeys);
    this.selectedIndex = -1;
  },
};
</script>

<style lang="scss">
.label-tag-menu {
  background: $color-white;
  border-radius: $border-radius-root;
  min-width: 270px;
  box-shadow: $shadow-tooltip;
  position: absolute;
  top: 0px;
  left: 44px;
  z-index: 6;

  &__header {
    display: flex;
    height: auto;
    font-weight: bold;
    font-size: 12px;
    padding: 12px 16px;
    border-bottom: 1px solid $color-border;
  }

  &__search-bar {
    align-items: center;
    display: flex;
    position: relative;
    width: 100%;
    justify-content: flex-start;
    padding: 8px 8px 2px 8px;
  }

  &__items {
    height: 350px;
    overflow-y: auto;
    padding: 8px;
    display: flex;
    flex-direction: column;
    gap: 4px;
    font-size: 14px;

    &::-webkit-scrollbar-track {
      background-color: $color-neutral-pale;
    }

    &::-webkit-scrollbar {
      height: 0px;
      width: 6px;
    }

    &::-webkit-scrollbar-thumb {
      border-radius: $border-radius-large;
      background-color: $color-grey-pale;
    }
  }

  &__item {
    cursor: pointer;
    padding: 8px;
    transition: all 200ms ease;
    border-radius: $border-radius-small;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-shrink: 0;
    height: auto;
    min-height: 36px;

    &:hover,
    &.is-selected {
      background-color: $color-hover;
      transition: all 200ms ease;
    }

    &.is-connected {
      color: $color-normal;
      outline: 1px solid rgba($color-text-annotation, 0.75);
      background: rgba($color-text-annotation, 0.3);
    }

    &-label {
      flex: 1;
      min-width: 0;
      white-space: normal;
      overflow-wrap: anywhere;
      word-break: break-word;
    }
  }

  &__buttons {
    display: flex;
    gap: 8px;
  }

  &__separator {
    border-bottom: $grey-border;
    height: 1px;
    min-height: 1px !important;
    margin-top: 1em !important;
    padding: 0;
  }

  &__append-item {
    &--content {
      padding: 8px;
      display: flex;
      height: auto;
    }

    &--button {
      text-decoration: underline;
      padding: 6px 8px;
      border-radius: $border-radius-small;
      cursor: pointer;
      transition: all 200ms ease;
      font-weight: bold;

      &:hover {
        background: $color-hover;
        transition: all 200ms ease;
      }
    }
  }
}

.submenu__header {
  display: flex;
  height: auto;
  font-weight: bold;
  font-size: 12px;
  padding: 12px 16px;
  border-bottom: 1px solid $color-border;
}

.submenu {
  position: absolute;
  background: $color-white;
  box-shadow: $shadow-default;
  top: 0;
  border-radius: $border-radius-root;
  min-width: 240px;
  z-index: auto !important;
  display: flex;
  flex-direction: column;
  left: 270px;

  &__items {
    height: 350px;
    overflow-y: auto;
    padding: 0.5rem;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  &__item {
    cursor: pointer;
    padding: 8px;
    transition: all 200ms ease;
    border-radius: $border-radius-small;
    display: flex;
    justify-content: space-between;
    align-items: center;

    &:hover {
      background-color: $color-hover;
      transition: all 200ms ease;
    }

    &.is-hover {
      background-color: $color-hover;
      transition: all 200ms ease;
    }

    &.is-connected {
      color: $color-normal;
      outline: 1px solid rgba($color-text-annotation, 0.75);
      background: rgba($color-text-annotation, 0.3);
    }

    &.is-disabled {
      background: $color-neutral-pale;
      color: $color-neutral;
      cursor: not-allowed;
    }
  }
}
</style>
